import {defineStore} from "pinia"
import type {ChatState, Contact, BeContact, Chat, BeChat, ChatConversation, BeConversation} from "@/types/chat.d"
import dayjs from "@/utils/dayjs";
import {useAuthStore} from "@/stores/auth"
import {type Ref} from "vue"
import { http } from "@/utils/http"
import { Logger } from "@/utils"

const API_URL = import.meta.env.VITE_API_URL

export const useChatStore = defineStore("chat", {
    state: (): ChatState => ({
        contacts: [],
        chat: [],
        me: null,
        member: null,
        chatId: ""
    }),
    actions: {
        setActiveChat(contact: Contact) {
            this.member = contact
            const chatId = this.chat.find(c => c.userId === contact.id)?.id
            this.chatId = chatId || ""
        },
        formatDate(date: Date) {
            return dayjs(date).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
                ? dayjs(date).format("HH:mm")
                : dayjs(date).format("D MMM")
        },

        addContact(member: BeContact) {
            const lastDate = new Date(member.lastDate)

            const contact = {
                id: member.id,
                name: member.fullName,
                jobTitle: "",
                avatar: member.imagePath,
                online: false,
                lastDate: lastDate,
                lastDateText: this.formatDate(lastDate),
                lastMessage: member.lastMessage || "",
                approved: member.approved,
                roleShort: member.displayRoleShort
            } as Contact

            this.contacts.push(contact)
        },
        addChat(item: BeChat, member: BeContact) {
            const authStore = useAuthStore()
            if (member.id === authStore.user.id) return

            const lastDate = new Date(member.lastDate)
            const chat = {
                id: item.id,
                userId: member.id,
                userObj: {
                    id: member.id,
                    name: member.fullName,
                    jobTitle: "",
                    avatar: member.imagePath,
                    online: false,
                    lastDate: lastDate,
                    lastDateText: this.formatDate(lastDate),
                    lastMessage: "",
                    approved: member.approved,
                    roleShort: member.displayRoleShort
                } as Contact,
                conversation: [],
                lastDate: lastDate,
                hasUnread: item.hasUnread ?? false
            } as Chat

            this.chat.push(chat)
        },
        buildMessageEntry(message: BeConversation): ChatConversation {
            const authStore = useAuthStore()
            const myId = authStore.user.id
            const lastDate = new Date(message.user.lastDate)
            return {
                id: message.id,
                isMine: message.user.id === myId,
                userId: message.user.id,
                userObj: {
                    id: message.user.id,
                    name: message.user.fullName,
                    jobTitle: "",
                    avatar: message.user.imagePath,
                    online: false,
                    lastDate: lastDate,
                    lastDateText: this.formatDate(lastDate),
                    lastMessage: "",
                    approved: message.user.approved,
                    roleShort: message.user.displayRoleShort
                } as Contact,
                messages: [message.bodytext],
                date: new Date(message.created)
            } as ChatConversation
        },
        addMessage(message: BeConversation) {
            const chatId = this.chatId
            const foundChat = this.chat.find((c: Chat) => c.id === chatId) as Chat
            foundChat.conversation.push(this.buildMessageEntry(message))
        },

        async fetchNavigation() {
            await this.fetchChats()
        },
        async fetchChats() {
            try {

                const authStore = useAuthStore()

                // Preserve cached conversations so we don't reload messages on every navigation
                const cachedConversations = new Map(this.chat.map(c => [c.id, c.conversation]))

                this.chat = []
                this.contacts = []

                const result = await http.get(API_URL + '/chat/list');

                for (const item of result) {
                    for (const member of item.members) {
                        if (member.id === authStore.user.id) continue
                        this.addChat(item, member)
                        if (!this.contacts.find(c => c.id === member.id)) {
                            this.addContact(member)
                        }
                    }
                }

                // Restore cached conversations
                for (const chat of this.chat) {
                    const cached = cachedConversations.get(chat.id)
                    if (cached?.length) chat.conversation = cached
                }

            } catch (error) {
                Logger.error(error)
            }
        },
        async fetchMessages() {
            try {

                // http://dev.ttpronet.local/api/chat/<id>
                /**
                 * {
                 *     "id": 2,
                 *     "created": "2024-12-03T20:19:22+00:00",
                 *     "title": "Krankenstand",
                 *     "members": [
                 *         {
                 *             "id": 1,
                 *             "roles": [
                 *                 "ROLE_USER",
                 *                 "ROLE_ADMIN",
                 *                 "ROLE_PLAYER_FREE"
                 *             ],
                 *             "firstname": "Super",
                 *             "lastname": "Admin",
                 *             "approved": false,
                 *             "imagePath": "http://dev.ttpronet.local/media/cache/user_small/uploads/media/user/0001/01/eec88826f5ff7477cb9b7aefef2a6a13755ae3a6.png"
                 *         },
                 *         {
                 *             "id": 20,
                 *             "roles": [
                 *                 "ROLE_PLAYER_PRO",
                 *                 "ROLE_USER",
                 *                 "ROLE_TRAINER"
                 *             ],
                 *             "firstname": "Obadja",
                 *             "lastname": "Hauser",
                 *             "approved": true,
                 *             "imagePath": "http://dev.ttpronet.local/media/cache/user_small/uploads/media/user/0001/01/e6e2263e434852debdca6d055873849792030675.png"
                 *         }
                 *     ],
                 *     "chatMessages": [
                 *         {
                 *             "id": 3,
                 *             "created": "2024-12-03T20:19:43+00:00",
                 *             "user": {
                 *                 "id": 1,
                 *                 "roles": [
                 *                     "ROLE_USER",
                 *                     "ROLE_ADMIN",
                 *                     "ROLE_PLAYER_FREE"
                 *                 ],
                 *                 "firstname": "Super",
                 *                 "lastname": "Admin",
                 *                 "approved": false,
                 *                 "imagePath": "http://dev.ttpronet.local/media/cache/user_small/uploads/media/user/0001/01/eec88826f5ff7477cb9b7aefef2a6a13755ae3a6.png"
                 *             },
                 *             "bodytext": "Ledier voll mit Fieber im Bett"
                 *         }
                 *     ]
                 * }
                 */

                const chatId = this.chatId
                const foundChat = this.chat.find((c: Chat) => c.id === chatId) as Chat

                if (foundChat) {

                    try {
                        // Always load all messages for the chat
                        const item = await http.get(`${API_URL}/chat/${this.chatId}/messages`)
                        foundChat.conversation = item.chatMessages.map(
                            (m: BeConversation) => this.buildMessageEntry(m)
                        )
                    } catch (error) {
                        if (error.message?.includes('404')) {
                            Logger.error('No messages found')
                        } else {
                            throw error
                        }
                    }

                }

            } catch (error) {
                Logger.error(error)
            }
        },
        async newMessage(postMessage: Ref<string>) {
            try {

                const chatId = this.chatId
                const authStore = useAuthStore()
                const myId = authStore.user.id
                const userId = this.member?.id || 0
                const foundChat = this.chat.find((c: Chat) => c.id === chatId) as Chat

                if (postMessage.value.length === 0)
                    return

                try {
                    const item = await http.post(API_URL + '/chat/add', {
                        body: {
                            chat: foundChat?.id || 0,
                            title: '',
                            bodytext: postMessage.value,
                            members: [userId]
                        }
                    });

                    Logger.debug('Message sent:', item)
                    postMessage.value = ""

                    if (item.chatMessages) {
                        if (!foundChat && item.members) {
                            for (const member of item.members) {
                                this.addChat(item, member)
                            }
                            this.chatId = item.id
                        }

                        for (const message of item.chatMessages) {
                            this.addMessage(message)
                        }
                    } else {
                        this.addMessage(item)
                    }
                } catch (error) {
                    Logger.error('Error sending message:', error)
                }

            } catch (error) {
                Logger.error(error)
            }
        },

        async fetchNewMessage(messageId: string | number) {
            const foundChat = this.chat.find((c: Chat) => c.id === this.chatId)
            if (!foundChat) return
            try {
                const message = await http.get(`${API_URL}/chat/message/${messageId}`)
                const alreadyPresent = foundChat.conversation.some(c => c.id === message.id)
                if (!alreadyPresent) {
                    const entry = this.buildMessageEntry(message)
                    entry.isNew = true
                    foundChat.conversation.push(entry)
                }
                // Update last message preview in contact list
                const contact = this.contacts.find(c => c.id === foundChat.userId)
                if (contact) {
                    contact.lastMessage = message.bodytext?.slice(0, 32) + (message.bodytext?.length > 32 ? '...' : '')
                    contact.lastDate = new Date(message.created)
                    contact.lastDateText = this.formatDate(contact.lastDate)
                }
            } catch (error) {
                Logger.error('fetchNewMessage error:', error)
            }
        },

        async markChatAsRead() {
            if (!this.chatId) return
            const foundChat = this.chat.find((c: Chat) => c.id === this.chatId)
            if (!foundChat?.hasUnread) return
            foundChat.hasUnread = false
            try {
                await http.post(`${API_URL}/chat/${this.chatId}/read`, {})
            } catch (error) {
                Logger.error('markChatAsRead error:', error)
            }
        },

        async startNewChat(memberId: string | number): boolean {
            try {

                let member = this.contacts.find(c => c.id === memberId)
                //const foundChat = this.chat.find(c => c.userId === memberId)

                if (!member) {
                    try {
                        const item = await http.get(API_URL + '/chat/member/' + memberId);
                        this.addContact(item)
                        member = this.contacts.find(c => c.id === memberId)
                    } catch (error) {
                        return false
                    }
                }

                this.setActiveChat(member as Contact)

            } catch (error) {
                Logger.error(error)
                return false
            }

            return true
        }
    },
    getters: {
        activeChat(): Chat | null {
            return this.chat.find(c => c.id === this.chatId) || null
        },
        activeMember(): Contact | null {
            return this.member || null
        }
    }
})
